import { useState, useRef, useEffect } from 'react';
import useSWR from 'swr';
import { localAxios } from '../api.js';
import ScreenHeader from '../components/ScreenHeader.js';

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

export default function Billing() {
    const [atTop,    setAtTop]    = useState(true);
    const [atBottom, setAtBottom] = useState(false);
    const tbodyWrapRef = useRef(null);

    const { data, isLoading, error } = useSWR('/api/billing', localAxios);
    const organization = data?.data?.organization ?? null;
    const sites = data?.data?.sites ?? [];
    const invoices = data?.data?.invoices ?? [];

    useEffect(() => {
        const el = tbodyWrapRef.current;
        if (!el) return;
        const check = () => {
            setAtTop(el.scrollTop <= 1);
            setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
        };
        check();
        el.addEventListener('scroll', check);
        return () => el.removeEventListener('scroll', check);
    }, [invoices.length]);

    const formatDate = (dt) => {
        if (!dt) return '—';
        return new Date(dt).toLocaleDateString('en-US', {
            year: '2-digit', month: 'numeric', day: 'numeric',
        });
    };

    const formatAmount = (amount, currency) => {
        if (amount === null || amount === undefined) return '—';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
    };

    const maskImage = (atTop && atBottom)
        ? 'none'
        : atTop
            ? 'linear-gradient(to bottom, black 70%, transparent 100%)'
            : atBottom
                ? 'linear-gradient(to bottom, transparent 0%, black 30%)'
                : 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)';

    const colGroup = (
        <colgroup>
            <col className="billing-col--invoice" />
            <col className="billing-col--amount" />
            <col className="billing-col--status" />
            <col className="billing-col--date" />
            <col className="billing-col--date" />
        </colgroup>
    );

    return (
        <div className="app-wide__container">
            <ScreenHeader message="Billing" />
            <div className="app-wide__add-new" />

            <div className="app-wide__main billing__main">
                {isLoading && (
                    <p className="reports-status">Loading billing information…</p>
                )}
                {error && (
                    <p className="reports-status reports-status--error">Could not load billing information.</p>
                )}

                {!isLoading && !error && !organization && (
                    <p className="reports-status">No billing account is set up for your organization yet.</p>
                )}

                {!isLoading && !error && organization && (
                    <>
                        <section className="billing-summary-card">
                            <h3 className="billing-summary-card__title">{organization.organization_name}</h3>
                            {organization.plan_name ? (
                                <div className="billing-summary-card__body">
                                    <div className="billing-summary-card__row">
                                        <span className="billing-summary-card__label">Plan</span>
                                        <span>{organization.plan_name}</span>
                                    </div>
                                    <div className="billing-summary-card__row">
                                        <span className="billing-summary-card__label">Price</span>
                                        <span>
                                            {formatAmount(organization.amount, organization.currency)}
                                            {' / '}
                                            {organization.billing_frequency === 'monthly' ? 'month' : 'year'}
                                        </span>
                                    </div>
                                    <div className="billing-summary-card__row">
                                        <span className="billing-summary-card__label">Status</span>
                                        <span className={`billing-status-badge billing-status-badge--${organization.subscription_status}`}>
                                            {capitalize(organization.subscription_status)}
                                        </span>
                                    </div>
                                    <div className="billing-summary-card__row">
                                        <span className="billing-summary-card__label">Next Billing Date</span>
                                        <span>{formatDate(organization.next_billing_date)}</span>
                                    </div>
                                </div>
                            ) : (
                                <p className="reports-status">No active subscription.</p>
                            )}
                        </section>

                        <section className="billing-section">
                            <h3 className="billing-section__title">Sites Covered</h3>
                            {sites.length === 0 ? (
                                <p className="reports-status">No sites are currently linked to this billing account.</p>
                            ) : (
                                <ul className="billing-sites__list">
                                    {sites.map(s => (
                                        <li key={s.id} className="billing-sites__item">{s.location_name}</li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        <section className="billing-section">
                            <h3 className="billing-section__title">Invoice History</h3>
                            <div className="reports-table-wrap billing-invoice-scroll">
                                <table className="reports-table">
                                    {colGroup}
                                    <thead>
                                        <tr>
                                            <th>Invoice #</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Issued</th>
                                            <th>Due</th>
                                        </tr>
                                    </thead>
                                </table>
                                <div
                                    ref={tbodyWrapRef}
                                    className="reports-tbody-wrap"
                                    style={{ maskImage, WebkitMaskImage: maskImage }}
                                >
                                    <table className="reports-table">
                                        {colGroup}
                                        <tbody>
                                            {invoices.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5} className="reports-table__empty">
                                                        No invoices yet.
                                                    </td>
                                                </tr>
                                            ) : invoices.map(inv => (
                                                <tr key={inv.id}>
                                                    <td>{inv.invoice_number ?? `#${inv.id}`}</td>
                                                    <td>{formatAmount(inv.amount_due, inv.currency)}</td>
                                                    <td>
                                                        <span className={`billing-status-badge billing-status-badge--${inv.status}`}>
                                                            {capitalize(inv.status)}
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(inv.issue_date)}</td>
                                                    <td>{formatDate(inv.due_date)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                    </>
                )}
            </div>

            <div className="app-wide__footer" />
        </div>
    );
}
